import React, { useEffect, useRef, useState } from "react";

import Icon from "../../../../components/Icon";
import Button from "../../../../components/Button";
import Input from "../../../../components/Input";
import InputSelect from "../../../../components/InputSelect";
import ButtonCheckbox from "../../../../components/ButtonCheckbox";
import { convertIntToMoney, convertMoenyToInt } from "../../../../helpers/conversions";

const isEmpty = value => (
    value === null || value === undefined || value === ''
    || (Array.isArray(value) && value.length === 0)
    || (typeof value === 'object' && !Array.isArray(value) && Object.values(value).every(item => item === null || item === ''))
);

/**
 * "Mais filtros" no estilo Conta Azul: a lista de filtros disponíveis abre um
 * editor pequeno de cada um. Cada tela passa as definições que fazem sentido
 * para ela:
 *
 * { key, label, type: 'options' | 'select' | 'money-range' | 'custom', options, render }
 *
 * - options: lista de checkboxes; valor é um array;
 * - select: um valor só, entre `options`;
 * - money-range: `{ min, max }` em centavos;
 * - custom: `render(value, setValue, setMeta)` desenha o editor; `setMeta`
 *   guarda o que a tela precisar para o chip (ex.: o nome do fornecedor).
 */
export const MoreFilters = ({ definitions, values, onApply }) => {
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState(null);
    const [meta, setMeta] = useState(null);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = event => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                close();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const close = () => {
        setOpen(false);
        setEditing(null);
    };

    const startEditing = definition => {
        setEditing(definition);
        setDraft(values[definition.key] ?? (definition.type === 'options' ? [] : definition.type === 'money-range' ? { min: null, max: null } : ''));
        setMeta(null);
    };

    const apply = () => {
        onApply(editing.key, isEmpty(draft) ? null : draft, meta);
        close();
    };

    const renderEditor = () => {
        if (editing.type === 'options') {
            const toggle = value => setDraft(draft.includes(value) ? draft.filter(item => item !== value) : [...draft, value]);

            return (
                <div className="filter-options">
                    {editing.options.map(option => (
                        <label key={option.value} className="filter-option">
                            <ButtonCheckbox checked={draft.includes(option.value)} onClick={() => toggle(option.value)} />
                            <span>{option.label}</span>
                        </label>
                    ))}
                </div>
            );
        }

        if (editing.type === 'select') {
            return <InputSelect label={editing.label} options={editing.options} value={draft || null} change={setDraft} />;
        }

        if (editing.type === 'money-range') {
            const toMoney = cents => (cents === null || cents === undefined || cents === '' ? 'R$ 0,00' : convertIntToMoney(cents, true));
            const fromMoney = text => {
                const cents = convertMoenyToInt(text);
                return cents > 0 ? cents : null;
            };

            return (
                <div className="filter-range">
                    <Input label={`De`} value={toMoney(draft.min)} change={text => setDraft({ ...draft, min: fromMoney(text) })} mask={`convertToMoney`} />
                    <Input label={`Até`} value={toMoney(draft.max)} change={text => setDraft({ ...draft, max: fromMoney(text) })} mask={`convertToMoney`} />
                </div>
            );
        }

        return editing.render(draft, setDraft, setMeta);
    };

    const activeCount = definitions.filter(definition => !isEmpty(values[definition.key])).length;

    return (
        <div className="finance-more-filters" ref={containerRef}>
            <button type="button" className={`more-filters-toggle${open ? ' open' : ''}`} onClick={() => (open ? close() : setOpen(true))}>
                Mais filtros
                {activeCount > 0 && <span className="more-filters-count">{activeCount}</span>}
                <Icon name="arrow-dropdown" size={16} />
            </button>

            {open &&
                <div className="more-filters-menu">
                    {!editing && definitions.map(definition => (
                        <button type="button" key={definition.key} className="more-filters-item" onClick={() => startEditing(definition)}>
                            {definition.label}
                            {!isEmpty(values[definition.key]) && <span className="more-filters-dot" />}
                        </button>
                    ))}

                    {editing &&
                        <div className="more-filters-editor">
                            <button type="button" className="more-filters-back" onClick={() => setEditing(null)}>
                                <Icon name="arrow-left" size={16} />
                                {editing.label}
                            </button>
                            <div className="more-filters-body">{renderEditor()}</div>
                            <Button type={`primary`} size={`small`} full={true} text={`Aplicar`} action={apply} />
                        </div>
                    }
                </div>
            }
        </div>
    );
};

export default MoreFilters;
